import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["checkbox", "totalCount", "selectedCount", "bulkActionContainer", "filter", "filterForm", "exportButton"]

  connect() {
    this.selectedItems = new Set();
    this.allSelected = false;

    this.boundUpdateCheckboxes = this.updateCheckboxes.bind(this);

    document.addEventListener("turbo:load", this.boundUpdateCheckboxes);

    // We use this instead of a Stimulus value because the table exists outside of a turbo-frame that gets refreshed with
    // new results.
    if (this.hasTotalCountTarget) {
      this.totalCount = parseInt(this.totalCountTarget.dataset.totalCountValue);
    }

    this.element.querySelector("turbo-frame")?.addEventListener("turbo:frame-render", () => {
      if (this.hasTotalCountTarget) {
        this.totalCount = parseInt(this.totalCountTarget.dataset.totalCountValue);
      }
    });

    if (this.hasBulkActionContainerTarget) {
      this.updateCheckboxes();
    }
  }

  disconnect() {
    document.removeEventListener("turbo:load", this.boundUpdateCheckboxes);
  }

  updateCheckboxes() {
    if (this.selectedItems.size > 0 || this.allSelected) {
      if (this.hasTotalCountTarget) {
        this.totalCountTarget.classList.add("hidden");
      }
      if (this.hasBulkActionContainerTarget) {
        this.bulkActionContainerTarget.classList.remove("hidden");
        this.bulkActionContainerTarget.classList.add("inline-flex");
        this.selectedCountTarget.textContent = this.allSelected ? (this.totalCount - this.selectedItems.size) + " selected" : this.selectedItems.size + " selected";
      }

      if (this.hasExportButtonTarget) {
        this.exportButtonTarget.classList.add("hidden");
      }
    } else {
      if (this.hasTotalCountTarget) {
        this.totalCountTarget.classList.remove("hidden");
      }
      if (this.hasBulkActionContainerTarget) {
        this.bulkActionContainerTarget.classList.add("hidden");
        this.bulkActionContainerTarget.classList.remove("inline-flex");
      }
      if (this.hasExportButtonTarget) {
        this.exportButtonTarget.classList.remove("hidden");
      }
    }

    this.checkboxTargets.forEach(checkbox => {
      if (this.allSelected) {
        checkbox.checked = !this.selectedItems.has(checkbox.dataset["itemIdParam"]);
      } else {
        checkbox.checked = this.selectedItems.has(checkbox.dataset["itemIdParam"]);
      }
    });
  }

  toggle(event) {
    const itemId = event.target.dataset["itemIdParam"];
    if (this.selectedItems.has(itemId)) {
      this.selectedItems.delete(itemId);
    } else {
      this.selectedItems.add(itemId);
    }
    this.updateCheckboxes();
  }

  toggleSelectAll(event) {
    this.selectedItems.clear();
    this.allSelected = !this.allSelected;
    this.updateCheckboxes();
  }

  bulkAction(event) {
    event.preventDefault();
    event.stopPropagation();

    if (event.params.url) {
      this.postSelectedIds(event.params.url, event.params.confirm);
      return;
    }

    const url = new URL("/dashboard/shared/background_actions/new", window.location.origin);

    url.searchParams.set("background_action[type]", event.params.action);
    url.searchParams.set("background_action[filters]", this.currentFiltersAsJson());

    if (this.allSelected) {
      url.searchParams.set("background_action[include_all]", "true");
      this.selectedItems.forEach(item => {
        url.searchParams.append("background_action[exclude_ids][]", item);
      });
    } else {
      this.selectedItems.forEach(item => {
        url.searchParams.append("background_action[include_ids][]", item);
      });
    }

    window.renderModal(url.toString());
  }

  // Posts the ids of the checked rows on this page; select-all covers the rows shown.
  postSelectedIds(url, confirmMessage) {
    const ids = this.checkboxTargets.filter(checkbox => checkbox.checked).map(checkbox => checkbox.dataset["itemIdParam"]);
    if (ids.length === 0) return;
    if (confirmMessage && !window.confirm(confirmMessage)) return;

    const form = document.createElement("form");
    form.method = "post";
    form.action = url;
    const fields = [["authenticity_token", document.querySelector("meta[name='csrf-token']")?.content], ...ids.map(id => ["subscriber_ids[]", id])];
    fields.forEach(([name, value]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = name;
      input.value = value;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.requestSubmit();
  }

  currentFiltersAsJson() {
    const filters = {};
    if (this.hasFilterFormTarget) {
      for (const [rawKey, value] of new FormData(this.filterFormTarget).entries()) {
        const nested = rawKey.match(/^([^[]+)\[([^\]]*)\]$/);

        if (rawKey.endsWith("[]")) {
          const key = rawKey.slice(0, -2);
          filters[key] ||= [];
          filters[key].push(value);
        } else if (nested) {
          // e.g. created_between[start] -> {created_between: {start: value}}, from Filters::DateRangeComponent
          const [, key, subkey] = nested;
          filters[key] ||= {};
          filters[key][subkey] = value;
        } else {
          filters[rawKey] = value;
        }
      }
    }

    return JSON.stringify(filters);
  }

  addFilter(event) {
    const filterInput = event.target.dataset["filterInputParam"];
    const form = event.target.closest("form");
    const input = document.createElement("input");
    input.setAttribute("type", "hidden");
    input.setAttribute("name", filterInput);
    input.setAttribute("value", "");
    form.appendChild(input);
    form.requestSubmit();
    const dropdown = this.application.getControllerForElementAndIdentifier(event.target.closest("[data-controller='dropdown']"), "dropdown");
    dropdown.close();
  }

  removeFilter(event) {
    // The actual changing of the filter happens in filter_controller.js,
    // we just need to reset the selected items and update the checkboxes.
    this.clearSelections();
  }

  clearSelections() {
    this.selectedItems.clear();
    this.allSelected = false;
    this.updateCheckboxes();
  }
}